import { Controller } from "@hotwired/stimulus"

// Stimulus controller for the Lead Quality scoring editor on Event Settings.
// Responsibilities:
//   * Debounced POST to lead_quality_preview endpoint, replaces previewSlot
//   * Dirty-form beforeunload guard
//   * Auto-focus first invalid input on submit failure
//   * Tier-ordering soft validation (Save disabled when high_value <= qualified)
//
// All preview rendering uses safe DOM construction (createElement +
// textContent), never innerHTML — even for server-returned data.
export default class extends Controller {
  static targets = ["form", "input", "previewSlot", "submit", "tierError"]
  static values  = { previewUrl: String }

  connect() {
    this._dirty = false
    this._scheduleHandle = null

    this._onBeforeUnload = (e) => {
      if (this._dirty) {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", this._onBeforeUnload)

    this._validateTierOrdering()
    // LRPO-5470 — checked once, synchronously, on load too: a page landed
    // on after a server 422 re-render (see below) can start with a blank
    // tier already in the DOM, and that's not a "mid-edit" state to wave
    // through — the debounce this check normally waits for exists to
    // protect ordinary typing, not an already-settled invalid page.
    this._validateTierCompleteness()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this._onBeforeUnload)
    if (this._scheduleHandle) clearTimeout(this._scheduleHandle)
  }

  schedulePreview(_event) {
    this._dirty = true
    this._validateTierOrdering()
    if (this._scheduleHandle) clearTimeout(this._scheduleHandle)
    this._scheduleHandle = setTimeout(() => {
      this._validateTierCompleteness()
      this._fetchPreview()
    }, 500)
  }

  // Disable Save when high_value <= qualified or qualified <= warm.
  //
  // LRPO-5264 — this used to run the ordering check against WHATEVER is
  // currently in the three tier fields, including mid-edit. A tier field
  // is briefly empty every time a user selects its text and types a
  // replacement (the normal way to change a two-digit number), and
  // parseInt("") is NaN. Every comparison against NaN is false, so that
  // transient blank read as "high_value <= qualified" and disabled Save —
  // for perfectly valid input, with only an invisible `title` tooltip
  // (nothing on-screen) to explain why. Now the ordering check is skipped
  // entirely until all three fields hold real numbers, and a real
  // violation renders a visible, `role="alert"` message instead of a
  // silent disable.
  _validateTierOrdering() {
    const hv = this._tierInput("high_value")
    const q  = this._tierInput("qualified")
    const w  = this._tierInput("warm")

    // Requiring ALL THREE fields to be finite before judging order means a
    // genuine violation can be transiently masked if an unrelated field
    // happens to be mid-edit at the same instant — acceptable: it self-heals
    // on the very next keystroke, whereas the old code's false-positive
    // disable didn't self-heal until the user finished typing correctly.
    // LRPO-5470's required-ness check picks up an incomplete state once
    // typing settles — see #_validateTierCompleteness.
    const complete = [hv, q, w].every(Number.isFinite)
    const ok = !complete || (hv > q && q > w)

    if (this.hasSubmitTarget) this.submitTarget.disabled = !ok
    this._renderTierError(ok ? null : "High Value must be greater than Qualified, which must be greater than Warm.")
  }

  // LRPO-5470 — a blank tier is a genuine server-side rejection now
  // (EventExhibitor#validate_tier_thresholds requires all three), so Save
  // must be disabled for it, matching the ordering check's treatment of a
  // real violation. Deliberately NOT run on every keystroke like
  // #_validateTierOrdering: a tier field is briefly blank on every
  // select-and-retype (see LRPO-5264 above), and disabling Save mid-
  // keystroke for that is exactly the false-positive LRPO-5264 fixed.
  // Instead this rides the SAME 500ms debounce as the preview fetch, so it
  // only judges "blank" once the user has paused — i.e. actually left a
  // tier empty, not passed through empty on the way to a real value.
  _validateTierCompleteness() {
    const hv = this._tierInput("high_value")
    const q  = this._tierInput("qualified")
    const w  = this._tierInput("warm")

    if ([hv, q, w].every(Number.isFinite)) return // complete — ordering check owns Save/message state

    if (this.hasSubmitTarget) this.submitTarget.disabled = true
    this._renderTierError("All three tier thresholds are required.")
  }

  _tierInput(tier) {
    const node = this.element.querySelector(`#tier_${tier}`)
    return node ? parseInt(node.value, 10) : NaN
  }

  // Shows/hides the visible tier-ordering error banner. `message` is
  // plain text (no interpolation of user input), so textContent is
  // sufficient — no innerHTML needed here either.
  _renderTierError(message) {
    if (!this.hasTierErrorTarget) return
    this.tierErrorTarget.textContent = message || ""
    this.tierErrorTarget.classList.toggle("d-none", !message)
  }

  async _fetchPreview() {
    if (!this.hasPreviewSlotTarget) return

    const formData = new FormData(this.formTarget)
    const config   = { weights: {}, tiers: {} }

    formData.forEach((value, name) => {
      const m = name.match(/event_exhibitor\[lead_quality_config\]\[(weights|tiers)\]\[([^\]]+)\](?:\[([^\]]+)\])?/)
      if (!m) return
      const section = m[1]
      const k1 = m[2]
      const k2 = m[3]
      if (k2) {
        config[section][k1] = config[section][k1] || {}
        config[section][k1][k2] = parseInt(value, 10)
      } else {
        config[section][k1] = parseInt(value, 10)
      }
    })

    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const res  = await fetch(this.previewUrlValue, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({ lead_quality_config: config })
      })
      if (!res.ok) return
      const data = await res.json()
      this._renderPreview(data)
    } catch (_e) {
      // network error — silently skip
    }
  }

  // Builds the preview panel via createElement + textContent ONLY.
  // No innerHTML anywhere. All numeric and tier-name values from JSON
  // are funneled through textContent so any unexpected payload string
  // can't introduce XSS.
  _renderPreview(data) {
    const wrapper = document.createElement("div")
    wrapper.className = "lead-quality-preview card mt-4"
    wrapper.setAttribute("role", "status")
    wrapper.setAttribute("aria-live", "polite")

    const header = document.createElement("div")
    header.className = "card-header"
    const headerStrong = document.createElement("strong")
    headerStrong.textContent = "Preview against this event's leads"
    header.appendChild(headerStrong)
    wrapper.appendChild(header)

    const body = document.createElement("div")
    body.className = "card-body"

    const summary = document.createElement("p")
    summary.className = "mb-2"
    summary.textContent = `With these rules, your ${Number(data.total_leads)} leads would be:`
    body.appendChild(summary)

    const list = document.createElement("ul")
    list.className = "list-unstyled small mb-2"
    Object.entries(data.tier_counts || {}).forEach(([tier, count]) => {
      const li = document.createElement("li")
      li.textContent = `${Number(count)} ${this._humanize(tier)}`
      list.appendChild(li)
    })
    body.appendChild(list)

    const deltas = Object.entries(data.delta_vs_current || {}).filter(([_t, d]) => d !== 0)
    if (deltas.length > 0) {
      const deltaP = document.createElement("p")
      deltaP.className = "text-muted small mb-0"
      const parts = deltas.map(([t, d]) => `${d > 0 ? "+" : ""}${Number(d)} ${this._humanize(t)}`).join(", ")
      deltaP.textContent = `vs current rules: ${parts}`
      body.appendChild(deltaP)
    }

    wrapper.appendChild(body)

    // Replace slot contents
    while (this.previewSlotTarget.firstChild) {
      this.previewSlotTarget.removeChild(this.previewSlotTarget.firstChild)
    }
    this.previewSlotTarget.appendChild(wrapper)
  }

  _humanize(snakeStr) {
    return String(snakeStr).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Called on form submit failure (server returns 422 with errors)
  focusFirstInvalid() {
    const first = this.element.querySelector("input.is-invalid, input[aria-invalid='true']")
    first?.focus()
  }
}
